import React from "react";
import { Phone, Mail, MapPin, Navigation } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { WhatsAppIcon } from "../components/layout/FloatingWhatsApp";
import { storeConfig, telHref, mailtoHref, mapsDirectionsUrl, mapsEmbedUrl } from "../config/store.config";
import { whatsappChatUrl } from "../lib/whatsapp";

const ContactCard = ({ name, phone, phoneIntl, email }) => (
  <div className="border border-ink p-6" data-testid={`contact-card-${name.split(" ")[0].toLowerCase()}`}>
    <h3 className="font-display text-2xl uppercase tracking-tight">{name}</h3>
    <div className="mt-4 space-y-2 text-sm">
      <a href={telHref(phone)} className="flex items-center gap-2.5 hover:text-brand-dark" data-testid={`contact-phone-${name.split(" ")[0].toLowerCase()}`}>
        <Phone size={16} /> {phoneIntl}
      </a>
      <a href={mailtoHref(email)} className="flex items-center gap-2.5 break-all hover:text-brand-dark" data-testid={`contact-email-${name.split(" ")[0].toLowerCase()}`}>
        <Mail size={16} /> {email}
      </a>
    </div>
    <div className="mt-5 grid grid-cols-3 gap-2">
      <a href={telHref(phone)} className="btn-outline px-2 py-2.5 text-[10px]">
        <Phone size={14} /> Call
      </a>
      <a href={whatsappChatUrl()} target="_blank" rel="noopener noreferrer" className="btn-base border border-ink bg-[#25D366] px-2 py-2.5 text-[10px] text-white hover:bg-[#1eb955]">
        <WhatsAppIcon size={14} /> Chat
      </a>
      <a href={mailtoHref(email)} className="btn-outline px-2 py-2.5 text-[10px]">
        <Mail size={14} /> Email
      </a>
    </div>
  </div>
);

const Contact = () => {
  useSeo({
    title: "Contact",
    description: "Get in touch with 9'One Store. Call, WhatsApp or email us, or find our store in Itarsi, Madhya Pradesh.",
    path: "/contact",
  });

  return (
    <div className="animate-fade-in">
      <div className="border-b border-ink">
        <div className="container-9one py-12">
          <p className="label-mono text-ink/50">Get in touch</p>
          <h1 className="mt-2 font-display text-5xl uppercase tracking-tight sm:text-7xl">Contact</h1>
        </div>
      </div>

      <div className="container-9one grid gap-10 py-12 lg:grid-cols-2">
        <div>
          <div className="border border-ink p-6">
            <h2 className="flex items-center gap-2 font-display text-2xl uppercase tracking-tight">
              <MapPin size={20} className="text-brand-dark" /> Visit Us
            </h2>
            <address className="mt-4 not-italic text-sm leading-relaxed text-ink/70">
              {storeConfig.storeName}<br />
              {storeConfig.address}<br />
              {storeConfig.city} – {storeConfig.pincode}<br />
              {storeConfig.state}, {storeConfig.country}
            </address>
            <a href={mapsDirectionsUrl} target="_blank" rel="noopener noreferrer" className="btn-solid mt-5 w-full" data-testid="get-directions-btn">
              <Navigation size={15} /> Get Directions
            </a>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <ContactCard
              name={storeConfig.primaryContactName}
              phone={storeConfig.primaryPhone}
              phoneIntl={storeConfig.primaryPhoneIntl}
              email={storeConfig.primaryEmail}
            />
            <ContactCard
              name={storeConfig.secondaryContactName}
              phone={storeConfig.secondaryPhone}
              phoneIntl={storeConfig.secondaryPhoneIntl}
              email={storeConfig.secondaryEmail}
            />
          </div>
        </div>

        {/* Map */}
        <div className="min-h-[400px] border border-ink" data-testid="contact-map">
          <iframe
            title="9'One Store location"
            src={mapsEmbedUrl}
            className="h-full min-h-[400px] w-full"
            style={{ border: 0, filter: "grayscale(1) contrast(1.1)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
};

export default Contact;
